import { Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { CONTACT, WA_DEFAULT_MSG, waLink } from "@/data/site";
import api from "@/lib/api";
import { Reveal, SectionHeading } from "@/components/Reveal";
import WhatsAppCTA from "@/components/WhatsAppCTA";

export default function Contact() {
  const logAndOpen = (wa, context) => {
    api.post("/enquiries", { context, page: "/contact" }).catch(() => {});
    window.open(waLink(WA_DEFAULT_MSG).replace("919602291176", wa), "_blank", "noopener");
  };

  return (
    <div data-testid="contact-page" className="bg-ivory">
      <section className="noise-overlay bg-obsidian pb-20 pt-40">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <SectionHeading
            light
            align="left"
            eyebrow="Contact & Enquiry"
            title="Have a Space in Mind? Let's Create It in Stone."
            description="Tell us about your project, idea or requirement — residential, hospitality, corporate, architectural or gifting. We usually reply within a few hours."
          />
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-4">
              <WhatsAppCTA
                label="Start a Project"
                message="Hello ShilpStones Creations, I have a space in mind and would like to start a project discussion."
                context="Contact page — Start a Project"
                testId="contact-page-start-project"
              />
              <WhatsAppCTA
                label="WhatsApp Us"
                context="Contact page — WhatsApp Us"
                variant="outline"
                testId="contact-page-whatsapp"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CONTACT.phones.map((p, i) => (
              <Reveal key={p.tel} delay={i * 0.1}>
                <div data-testid={`contact-phone-card-${i}`} className="flex h-full flex-col rounded-2xl border border-stone-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_20px_50px_-20px_rgba(154,123,44,0.3)]">
                  <Phone className="h-6 w-6 text-gold-dark" />
                  <p className="mt-5 text-[11px] uppercase tracking-[0.25em] text-stone-500">Call or WhatsApp</p>
                  <a href={`tel:${p.tel}`} className="mt-2 font-serif text-2xl text-stone-900 transition-colors hover:text-gold-dark">
                    {p.label}
                  </a>
                  <button
                    onClick={() => logAndOpen(p.wa, `Contact card ${p.label}`)}
                    data-testid={`contact-card-whatsapp-${i}`}
                    className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-obsidian transition-all hover:-translate-y-0.5"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    Chat Now
                  </button>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-card"
                className="flex h-full flex-col rounded-2xl border border-stone-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_20px_50px_-20px_rgba(154,123,44,0.3)]"
              >
                <Mail className="h-6 w-6 text-gold-dark" />
                <p className="mt-5 text-[11px] uppercase tracking-[0.25em] text-stone-500">Email</p>
                <p className="mt-2 break-all font-serif text-xl text-stone-900">{CONTACT.email}</p>
                <span className="mt-6 text-[11px] font-semibold uppercase tracking-[0.15em] text-gold-dark">
                  Write to us
                </span>
              </a>
            </Reveal>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {CONTACT.addresses.map((a, i) => (
              <Reveal key={a.label} delay={i * 0.1}>
                <div data-testid={`contact-address-card-${i}`} className="flex h-full gap-4 rounded-2xl border border-stone-200 bg-white p-8">
                  <MapPin className="h-6 w-6 shrink-0 text-gold-dark" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.25em] text-stone-500">{a.label}</p>
                    <p className="mt-2 font-serif text-xl text-stone-900">{a.value}</p>
                    <p className="mt-3 flex items-center gap-2 text-xs text-stone-500">
                      <Clock className="h-3.5 w-3.5 text-gold-dark" />
                      Open Mon – Sat, 10:00 AM – 7:30 PM
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="spotlight-frame mt-6 overflow-hidden rounded-2xl">
              <iframe
                title="ShilpStones Creations — Udaipur location map"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Sukher,+Udaipur,+Rajasthan,+India&output=embed"
                className="h-96 w-full border-0"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
